import { SLA_DEFS, SLA_META } from '../seed/wms-master-data.seed'
import { readTable } from '../store/localDb.store'

/**
 * Pembaca ambang Master SLA (MST-08, tabel `sla_wms_setting`). Handler yang menghitung keterlambatan / eskalasi / aturan
 * otomatis memakai fungsi ini — bukan angka mati — sehingga mengubah nilai di Master SLA langsung mengubah perilaku papan
 * kerja, dashboard dan laporan, dan halaman FE (countdown) membaca baris yang sama lewat endpoint. Bila baris belum ada
 * (seed lama) nilai bawaan seed dipakai.
 *
 * Aturan otomatis (laporan 28 §9.1): `slaEnabled(code)` = `is_enabled`; mati berarti aturan tidak pernah berjalan dan
 * langkahnya menunggu aksi manual. `slaScope(code)` = opsi cakupan (`scope_json`).
 */

const UNIT_MINUTES: Record<string, number> = { MINUTE: 1, HOUR: 60, DAY: 1440, PERCENT: 1 }

const rowOf = (code: string) => readTable('sla_wms_setting').find(item => item.code === code)

/** Nilai mentah sesuai satuan barisnya (jam untuk HOUR, hari untuk DAY, dst.). Nol sah hanya bila `allowZero` (H+0). */
export function slaValue(code: string): number {
  const row = rowOf(code)
  const value = Number(row?.value)
  if (row && Number.isFinite(value) && (value > 0 || (value === 0 && SLA_META[code]?.allowZero))) return value
  return SLA_DEFS.find(def => def[0] === code)?.[4] ?? 0
}

export function slaUnit(code: string): string {
  return String(rowOf(code)?.unit ?? SLA_DEFS.find(def => def[0] === code)?.[5] ?? '')
}

/** Ambang dalam menit (HOUR × 60, DAY × 1440). */
export const slaMinutes = (code: string) => slaValue(code) * (UNIT_MINUTES[slaUnit(code)] ?? 1)
/** Ambang dalam jam. */
export const slaHours = (code: string) => slaMinutes(code) / 60
/** Ambang dalam hari. */
export const slaDays = (code: string) => slaMinutes(code) / 1440

/** Aturan otomatis menyala? Baris belum ada → menyala (bawaan). Hanya baris yang boleh dimatikan yang dapat bernilai false. */
export function slaEnabled(code: string): boolean {
  if (!SLA_META[code]?.toggle) return true
  const row = rowOf(code)
  return row ? row.is_enabled !== false : true
}

/** Opsi cakupan baris (`scope_json`) dengan bawaan seed untuk kunci yang belum tersimpan. */
export function slaScope(code: string): Record<string, boolean> {
  const defaults = Object.fromEntries((SLA_META[code]?.scope ?? []).map(def => [def.key, def.default]))
  const stored = rowOf(code)?.scope_json
  return { ...defaults, ...(stored && typeof stored === 'object' ? (stored as Record<string, boolean>) : {}) }
}

/** Pembaca aturan Auto-approve ALM (GAB-01 / GAB-02 / sweep). */
export const almAutoApprove = () => ({
  enabled: slaEnabled('GAB_ALM_AUTO_APPROVE'),
  minutes: slaMinutes('GAB_ALM_AUTO_APPROVE'),
  value: slaValue('GAB_ALM_AUTO_APPROVE'),
  unit: slaUnit('GAB_ALM_AUTO_APPROVE')
})

/** Pembaca aturan Auto-approve hasil sortir claim (dikonsumsi alur claim — batch C). */
export const clmAutoApprove = () => ({
  enabled: slaEnabled('CLM_AUTO_APPROVE'),
  minutes: slaMinutes('CLM_AUTO_APPROVE'),
  value: slaValue('CLM_AUTO_APPROVE'),
  unit: slaUnit('CLM_AUTO_APPROVE'),
  includeNotGood: slaScope('CLM_AUTO_APPROVE').include_not_good !== false
})

/** Jendela H+n selisih opname per gudang: [eskalasi ke Kadiv, batas approval] dalam hari kalender. */
export const dscWindow = (wh: 'RM' | 'FG'): [number, number] => [
  slaDays(`DSC_ESCALATE_${wh}`),
  slaDays(`DSC_APPROVAL_${wh}`)
]

/**
 * Disposisi ke Kadiv (v7.50 C-13): `null` bila disposisinya dimatikan di Master SLA (keputusan tidak pernah naik ke
 * Kadiv), selain itu ambangnya dalam jam. Dipakai Penerimaan FG (`FG_APPROVE_ESCALATE`), Release order
 * (`OFG_RELEASE_ESCALATE`) dan disposisi Not Good (`DSP_APPROVE_ESCALATE` — belum ada konsumen layar).
 */
export const escalateHours = (code: 'FG_APPROVE_ESCALATE' | 'OFG_RELEASE_ESCALATE' | 'DSP_APPROVE_ESCALATE') =>
  slaEnabled(code) ? slaHours(code) : null
